import SubsetCandidateScreenModal from '@/components/Base/modals/SubsetCandidateScreenModal'
import { formatDisplayValue } from '@/constants/common.constant'
import { useAppSelector } from '@/hooks/use-app-selector'
import { setSelectedPortfolios } from '@/states/portfolioOptmization.slice'
import React, { useCallback, useState } from 'react'
import { useDispatch } from 'react-redux'

interface Security {
  id: string
  securityId: string
  description: string
  coupon: string
  price: string
  country: string
  currency: string
  rating: string
  maturityDate: string
  sector: string
  outstanding: string
}


interface Portfolio {
  name: string
  date: string
  currency: string
  benchmark: string
  benchmarkDescription?: string
  totalSecurities?: number
  [key: string]: any
}

interface SecurityAccordionProps {
  securities?: Security[]
  onAddSecurities?: (securities: Security[]) => void
  onTransferToWhatIf?: (securities: Security[]) => void
  onClearSecurities?: () => void
  loading?: boolean
  children?: React.ReactNode
  selectedPortfolio?: Portfolio
}

const SecurityAccordion: React.FC<SecurityAccordionProps> = ({
  securities = [],
  onAddSecurities,
  onTransferToWhatIf,
  onClearSecurities,
  loading = false,
  children,
  selectedPortfolio
}) => {
  const [isCriteriaModalOpen, setIsCriteriaModalOpen] = useState(false)
  
  // Redux setup for PO CriteriaModal
  const dispatch = useDispatch()
  const portfolioOptimization = useAppSelector((s) => s.portfolioOptimization)

  // Handle opening criteria modal
  const handleOpenCriteriaModal = useCallback(() => {
    // Set up portfolio context for PO modal
    const currentDate = selectedPortfolio?.date || new Date().toISOString().slice(0, 10).replace(/-/g, '')
    let portfolioDate = currentDate
    
    // Convert MM/DD/YYYY to YYYYMMDD format if needed
    if (portfolioDate.includes('/')) {
      const [month, day, year] = portfolioDate.split('/')
      portfolioDate = `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`
    }
    
    const portfolioForScreening = {
      name: formatDisplayValue(selectedPortfolio?.name),
      description: selectedPortfolio?.name ? `Portfolio: ${selectedPortfolio.name}` : formatDisplayValue(null),
      benchmarkName: formatDisplayValue(selectedPortfolio?.benchmark),
      benchmarkDescription: formatDisplayValue(selectedPortfolio?.benchmarkDescription),
      dates: [portfolioDate],
      date: portfolioDate,
      type: formatDisplayValue(selectedPortfolio?.type),
      numOfSecurities: selectedPortfolio?.totalSecurities || securities?.length || 0,
      currency: formatDisplayValue(selectedPortfolio?.currency),
      user: undefined,
      constraints: undefined,
      marketValue: formatDisplayValue(selectedPortfolio?.marketValue),
    }
    
    dispatch(setSelectedPortfolios([portfolioForScreening]))
    setIsCriteriaModalOpen(true)
  }, [securities, dispatch, selectedPortfolio])

  // Handle closing criteria modal
  const handleCloseCriteriaModal = useCallback(() => {
    setIsCriteriaModalOpen(false)
  }, [])


  return (
    <>
      {/* Render children (like AddSecurities component) with modal trigger */}
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          return React.cloneElement(child, {
            onOpenModal: handleOpenCriteriaModal,
            onTransferToWhatIf: onTransferToWhatIf,
            onClearSecurities: onClearSecurities
          } as any)
        }
        return child
      })}

      {/* What-If Screening Modal - Uses SubsetCandidateScreenModal */}
      <SubsetCandidateScreenModal
        isOpen={isCriteriaModalOpen}
        onClose={handleCloseCriteriaModal}
        isSubset={true}
        onApply={(criteriaName: string, criteriaData?: any) => {
          // Close the screening modal
          handleCloseCriteriaModal()
          
          console.log('Received criteriaData from What-If modal:', criteriaData)
          
          // Extract securities from What-If modal data
          let screenedSecurities: Security[] = []
          
          if (criteriaData?.securities && Array.isArray(criteriaData.securities)) {
            screenedSecurities = criteriaData.securities.map((security: any, index: number) => ({
              id: security.id || `security-${Date.now()}-${index}`,
              securityId: security.securityId || security.id || '',
              price: security.price ? parseFloat(security.price).toFixed(2) : formatDisplayValue(security.price),
              country: formatDisplayValue(security.country),
              currency: formatDisplayValue(security.currency),
              rating: formatDisplayValue(security.moody || security.rating),
              maturityDate: formatDisplayValue(security.maturityDate),
              sector: formatDisplayValue(security.sector),
              outstanding: formatDisplayValue(security.outstanding)
            }))
          }
          
          console.log(`Found ${screenedSecurities.length} securities from What-If modal:`, screenedSecurities)
          
          // Directly add securities to main grid
          if (onAddSecurities && screenedSecurities.length > 0) {
            onAddSecurities(screenedSecurities)
          }
        }}
      />
    </>
  )
}

export default SecurityAccordion
